import React from "react";
import {
  ButtonColour, Typography, TypographyVariants, ButtonSize,
  Accordion,
} from "@trussle/external-components";

import classNames from "classnames";
import { AccordionSimple } from "../../AccordionSimple";
import { AccordionDesktop } from "../../AccordionDesktop";
import { AccordionVariant, IAccordionItemRaw } from "../../../types/IAccordionItem";
import { IContentfulBackgroundColour } from "../../../types/IContentfulBackgroundColour";
import { IContentfulRichTextResponse } from "../../../types/IContentfulRichText";
import { ICTA } from "../../../types/ICTA";
import { addTracking, CallToActions } from "../../CallToActions";
import { RichText } from "../../RichText";
import { generateId } from "../../../helpers/generateId";
import { transformRichText } from "../../../helpers/richText";

import "./styles.scss";
import { PanelWrapper } from "../../PanelWrapper";
import AccordionMobile from "../../AccordionMobile";
import { useIsMobile } from "../../../hooks/useIsMobile";

export enum AccordianTextAlign {
  CENTER = "Center",
  LEFT = "Left",
  RIGHT = "Right",
}

export interface IAccordionPanelProps {
  accordion: IAccordionItemRaw[];
  accordionSecondary?: IAccordionItemRaw[];
  backgroundColour?: IContentfulBackgroundColour;
  title?: string;
  titleSecondary?: string;
  content?: IContentfulRichTextResponse;
  callToActions?: ICTA[];
  textAlign?: AccordianTextAlign;
  headingType?: string;
  headerAnchor?: string;
  betterV2?: boolean;
  simple?: boolean;
  variant?: "default" | "betterV2" | "betterV2Simple";
  firstTabVariant?: AccordionVariant;
  secondTabVariant?: AccordionVariant;
  accordionTitle?: string;
  accordionTitleSecondary?: string;
  snapScroll?: boolean;
}

export const bgColourToContentColour = (bgColour: string) => {
  switch (bgColour) {
    case "grey":
    case "lime-green":
    case "pink":
    case "sky-blue":
      return "white";
    default:
      return "lime-green";
  }
};

export const AccordionPanel = (props: IAccordionPanelProps) => {
  const transformedProps = {
    ...props,
    accordion: props.accordion.map((a) => ({
      ...a,
      content: transformRichText(a.content.raw),
    })),
    accordionSecondary: props.accordionSecondary?.map((a) => ({
      ...a,
      content: transformRichText(a.content.raw),
    })),
    content: props.content ? transformRichText(props.content.raw) : null,
  };
  const {
    backgroundColour = "white",
    title,
    content,
    accordion,
    accordionSecondary,
    callToActions: ctas,
    textAlign,
    headingType,
    headerAnchor,
    variant,
    firstTabVariant,
    secondTabVariant,
    accordionTitle,
    accordionTitleSecondary,
    titleSecondary,
    snapScroll,
  } = transformedProps;

  const secondaryButtonColour = backgroundColour === "grey" ? ButtonColour.TertiaryLight : ButtonColour.Secondary;

  const accordionItems = accordion.map((item) => (
    {
      title: item.title,
      secondaryTitle: item.secondaryTitle,
      content:
  <RichText
    content={item.content}
  />,
    }
  ));

  const accordionItemsSecondary = accordionSecondary ? accordionSecondary.map((item) => (
    {
      title: item.title,
      secondaryTitle: item.secondaryTitle,
      content:
    <RichText
      content={item.content}
    />,
    }
  )) : undefined;

  const isMobile = useIsMobile();

  const propsV2 = {
    accordion: accordionItems,
    accordionSecondary: accordionItemsSecondary,
    variant,
    firstTabVariant,
    secondTabVariant,
    accordionTitle,
    accordionTitleSecondary,
    title,
    titleSecondary,
    snapScroll,
  };
  const isBetterV2 = variant === "betterV2";
  const isBetterV2Simple = variant === "betterV2Simple";

  return (
    <PanelWrapper
      backgroundColour={backgroundColour}
      className={classNames({
        "accordionPanel__no-padding": isBetterV2 && !isBetterV2Simple,
      })}
      isBetterV2={isBetterV2Simple}
    >
      {isBetterV2 || isBetterV2Simple
        ? (!isBetterV2Simple ? (
          isMobile ? <AccordionMobile {...propsV2} /> : <AccordionDesktop {...propsV2} />
        ) : <AccordionSimple {...propsV2} />)
        : (
          <div
            id={headerAnchor}
            className={`accordion__text-align-${
              textAlign
                ? textAlign.toLowerCase()
                : AccordianTextAlign.LEFT.toLowerCase()
            }`}
          >
            <div className="accordionPanel__content">
              {title && (
              <Typography
                {...(title && {
                  id: generateId(title),
                })}
                classes="accordionPanel__header"
                variant={headingType as TypographyVariants || "h3"}
              >
                {title}
              </Typography>
              )}
              {content && (
              <Typography variant="body1" classes="styledRichTextContainer">
                <RichText
                  content={content}
                />
              </Typography>
              )}
            </div>
            <div className="accordionPanel__accordion">
              <Accordion items={accordionItems} />
            </div>
            {ctas && (
            <div className="accordionPanel__ctas">
              <CallToActions
                alignment="center"
                secondaryCtaColour={secondaryButtonColour}
                size={ButtonSize.Large}
                ctas={addTracking("Accordion", ...ctas)}
              />
            </div>
            )}
          </div>
        )}

    </PanelWrapper>
  );
};
